import {
  AuditLog,
  RoutineIdpStatus,
  WorkflowQuestionConfig,
  WorkflowResponseState,
  isNonTerminalIdpStatus,
} from '@gripp/shared-logic';
import { useMemo } from 'react';
import { View } from 'react-native';
import { Text } from 'react-native-paper';
import { routineMessageStyles } from '../../components/routineMessageStyles';
import { isIdpManagedQuestion } from '../admin/idpQuestions';
import { RoutineQuestionModules } from '../questions/routineQuestionModules';
import { RoutineQuestionMessageProps } from '../questions/types';
import { IdpExtractingValue } from './idpMessageStates';

type WorkflowMessageQuestionHeaderProps<
  C extends WorkflowQuestionConfig,
  S extends WorkflowResponseState
> = RoutineQuestionMessageProps<C, S> & {
  index: number;
  auditLog?: AuditLog[];
  /** Routine-level IDP toggle. A question's own `idpManaged` flag survives an
   * IDP off-toggle, so both halves are required before this renders anything
   * IDP-specific (same composite gate as the execution side). */
  idpEnabled?: boolean;
  /** Live extraction status off the polled `data.idp` — non-terminal swaps the
   * value for a processing indicator, terminal reveals it. */
  idpStatus?: RoutineIdpStatus;
};

export const WorkflowMessageQuestion = (
  props: WorkflowMessageQuestionHeaderProps<any, any>
) => {
  const module = useMemo(() => {
    return RoutineQuestionModules[
      props.question.type as keyof typeof RoutineQuestionModules
    ];
  }, [props.question.type]);
  const Message = useMemo(() => module.message, [module]);
  const hasExceptions = useMemo(
    () => props.response?.exceptionCount && props.response.exceptionCount > 0,
    [props.response?.exceptionCount]
  );

  const isEdited = useMemo(
    () =>
      props?.auditLog?.some(
        (au) => au.questionId === props.response.questionId
      ),
    [props.auditLog, props.response]
  );

  const isIdpManaged = useMemo(
    () => !!props.idpEnabled && isIdpManagedQuestion(props.question),
    [props.idpEnabled, props.question]
  );
  // Extraction still running: the label stands, the value does not exist yet.
  const isExtracting = isIdpManaged && isNonTerminalIdpStatus(props.idpStatus);

  return (
    <View
      style={[
        routineMessageStyles.questionContainer,
        hasExceptions
          ? routineMessageStyles.exceptionsContainer
          : routineMessageStyles.noExceptionsContainer,
        isEdited ? routineMessageStyles.isEdited : {},
      ]}
    >
      <Text
        style={[
          hasExceptions
            ? routineMessageStyles.questionLabelException
            : routineMessageStyles.questionLabel,
          isEdited ? routineMessageStyles.isEditedText : {},
        ]}
      >
        {props.index}. {props.question.instructions}
      </Text>
      {isExtracting ? (
        <IdpExtractingValue testID={`idp-extracting-${props.question.id}`} />
      ) : (
        <Message
          {...props}
          textStyle={[
            hasExceptions
              ? routineMessageStyles.textException
              : routineMessageStyles.text,
            isEdited ? routineMessageStyles.isEditedText : {},
          ]}
        />
      )}
    </View>
  );
};
